import React, { useState, useEffect } from 'react';
import { fetchData, deleteIssue } from '../../services/apiService';
import './dashboard.css';
import Update from '../update/Update';
import ContextMenu from '../contextMenu/ContextMenu';
import DeleteConfirmationModal from '../delete/Delete'; 

const Dashboard = () => {
    const [issues, setIssues] = useState([]);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
    const [showContextMenu, setShowContextMenu] = useState(false);

    const today = new Date();

    const formatDate = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        return date.toISOString().slice(0,10);
    };

    const loadData = async () => {
        try {
            const listData = await fetchData();
            const formattedData = listData.map(issue => ({
                ...issue,
                DueDate: formatDate(issue.DueDate),
                DateReported: formatDate(issue.DateReported)
            }));
            setIssues(formattedData);
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleRowClick = (issue) => {
        setSelectedIssue(issue);
        setShowUpdateModal(true);
    };

    const handleRowRightClick = (issue, event) => {
        event.preventDefault();
        setSelectedIssue(issue);
        setSelectedId(issue.id);
        setContextMenuPosition({ x: event.pageX, y: event.pageY });
        setShowContextMenu(true);
    };

    const handleClickOutside = () => {
        setShowContextMenu(false);
        setSelectedId(null);
    };

    const handleUpdateClick = () => {
        setShowContextMenu(false);
        setShowUpdateModal(true);
    };

    const handleDeleteClick = () => {
        setShowContextMenu(false);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteIssue(selectedIssue);
            setShowDeleteModal(false);
            setIssues(prev => prev.filter(issue => issue.id !== selectedIssue.id));
            setSelectedIssue(null);
        } catch (error) {
            console.error('Error deleting issue:', error);
        }
    };

    const reloadData = () => {
        loadData();
        setShowUpdateModal(false);
    };

    const isDueSoon = (issue) => {
        if (!issue.DueDate) return false;
        const date = new Date(issue.DueDate);
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 7 && issue.Status !== 'Closed' && issue.Status !== 'Resolved';
    };

    const highSeverityIssues = issues
        .filter(issue => issue.Severity === 'High' && issue.Status !== 'Closed' && issue.Status !== 'Resolved')
        .sort((a, b) => {
            if (!a.DueDate) return -1;
            if (!b.DueDate) return 1;
            return new Date(a.DueDate) - new Date(b.DueDate);
        });

    const upcomingIssues = issues
        .filter(issue => isDueSoon(issue))
        .sort((a, b) => new Date(a.DueDate) - new Date(b.DueDate));

    const getDueDateClass = (dueDate) => {
        if (!dueDate) return '';
        const date = new Date(dueDate);
        const diffTime = date - today;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return 'due-today';
        } else if (diffDays < 0) {
            return 'due-past';
        } else if (diffDays <= 7) {
            return 'due-soon';
        } else {
            return 'due-future';
        }
    };

    return (
        <main className="dashboard-container" onClick={handleClickOutside}>
            <div className="section">
                <h2>Tasks Due Soon (Next 7 Days)</h2>
                <h3>({upcomingIssues.length}) issues late/upcoming</h3>
                <div className="issues-table-container">
                    {upcomingIssues.length > 0 ? (
                        <table className="issues-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Job #</th>
                                    <th>Issue Name</th>
                                    <th>Assigned To</th>
                                    <th>Due Date</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {upcomingIssues.map((issue) => (
                                    <tr
                                        key={issue.id}
                                        onClick={() => handleRowClick(issue)}
                                        onContextMenu={(e) => handleRowRightClick(issue, e)}
                                        className={`issue-row ${selectedId === issue.id ? 'row-selected' : ''}`}
                                    >
                                        <td>{issue.id}</td>
                                        <td><strong>{issue.Title}</strong></td>
                                        <td>{issue.IssueName || ''}</td>
                                        <td>
                                            {typeof issue.AssignedToName === 'string'
                                                ? issue.AssignedToName.split(',').map((name, index) => (
                                                    <React.Fragment key={index}>
                                                        {name.trim()}
                                                        <br />
                                                    </React.Fragment>
                                                ))
                                                : issue.AssignedToName || ''}
                                        </td>
                                        <td>
                                            <span className={getDueDateClass(issue.DueDate)}>
                                                {issue.DueDate || ''}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`severity-${issue.Severity.toLowerCase()}`}>
                                                {issue.Severity || ''}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`status-${issue.Status.toLowerCase()}`}>
                                                {issue.Status || ''}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No upcoming tasks due within the next 7 days</p>
                    )}
                </div>
            </div>

            <div className="section">
                <h2>High Severity Issues</h2>
                <h3>({highSeverityIssues.length}) high priority issues</h3>
                <div className="issues-table-container">
                    {highSeverityIssues.length > 0 ? (
                        <table className="issues-table">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Job #</th>
                                    <th>Issue Name</th>
                                    <th>Assigned To</th>
                                    <th>Due Date</th>
                                    <th>Severity</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {highSeverityIssues.map((issue) => (
                                    <tr
                                        key={issue.id}
                                        onClick={() => handleRowClick(issue)}
                                        onContextMenu={(e) => handleRowRightClick(issue, e)}
                                        className={`issue-row ${selectedId === issue.id ? 'row-selected' : ''}`}
                                    >
                                        <td>{issue.id}</td>
                                        <td><strong>{issue.Title}</strong></td>
                                        <td>{issue.IssueName || ''}</td>
                                        <td>
                                            {typeof issue.AssignedToName === 'string'
                                                ? issue.AssignedToName.split(',').map((name, index) => (
                                                    <React.Fragment key={index}>
                                                        {name.trim()}
                                                        <br />
                                                    </React.Fragment>
                                                ))
                                                : issue.AssignedToName || ''}
                                        </td>
                                        <td>
                                            <span className={getDueDateClass(issue.DueDate)}>
                                                {issue.DueDate || ''}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`severity-${issue.Severity.toLowerCase()}`}>
                                                {issue.Severity || ''}
                                            </span>
                                        </td>
                                        
                                        <td>
                                            <span className={`status-${issue.Status.toLowerCase()}`}>
                                                {issue.Status || ''}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <p>No high severity issues found.</p>
                    )}

                    {showContextMenu && (
                        <ContextMenu
                            x={contextMenuPosition.x}
                            y={contextMenuPosition.y}
                            onUpdate={handleUpdateClick}
                            onDelete={handleDeleteClick}
                        />
                    )}

                    {showUpdateModal && selectedIssue && (
                        <div className="modal">
                            <div className="modal-content">
                                <span
                                    className="close"
                                    onClick={() => setShowUpdateModal(false)}
                                >
                                    &times;
                                </span>
                                <Update
                                    issue={selectedIssue}
                                    closeModal={() => {
                                        setShowUpdateModal(false);
                                        reloadData();
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {showDeleteModal && (
                        <DeleteConfirmationModal
                            show={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            onConfirm={confirmDelete}
                        />
                    )}
                </div>
            </div>
        </main>
    );
};

export default Dashboard;
